/**
 * Reset Password Request Page
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Input,
  Label,
  Button,
} from "@jourfx/ui";
import { Loader2, CheckCircle } from "lucide-react";
import { useBrandLogoUrl } from "@/branding/BrandingProvider";

export const Route = createFileRoute("/reset-password/")({
  component: ResetPasswordRequestPage,
});

function ResetPasswordRequestPage() {
  // Force light mode on auth pages
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains("dark");
    root.classList.remove("dark");
    root.classList.add("light");
    return () => {
      if (wasDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  const primaryLogoUrl = useBrandLogoUrl("primary");
  const requestReset = useMutation(api.auth.passwordReset.request);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await requestReset({ email });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Senden");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-12 bg-[hsl(145_15%_95%)]">
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <img src={primaryLogoUrl} alt="JourFx" className="h-10 w-auto" />
        </div>
        <Card className="border border-border/50 bg-card shadow-xl">
          <CardHeader className="text-center">
            <CardTitle>Passwort zurücksetzen</CardTitle>
            <CardDescription>
              Geben Sie Ihre E-Mail-Adresse ein, um einen Reset-Link zu erhalten.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {success ? (
                <div className="flex flex-col items-center gap-2 text-center text-emerald-600">
                  <CheckCircle className="h-8 w-8" />
                  <p className="text-sm text-muted-foreground">
                    Wenn ein Konto existiert, wurde ein Reset-Link gesendet.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail-Adresse</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                </>
              )}
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              {!success && (
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Reset-Link senden
                </Button>
              )}
              <Link
                to="/login"
                search={{ redirect: "/dashboard" }}
                className="text-sm text-primary hover:underline"
              >
                Zurück zur Anmeldung
              </Link>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
